/**
 * Gate for every /learn route — Aurora Atlas.
 *
 * Renders one of five states, in priority order: still loading, signed out,
 * blocked (unverified / pending / disabled), profile-unavailable, or the page
 * itself. The blocked states stay distinct screens rather than a generic error
 * because each has a different action — confirm the email, wait for approval,
 * or contact an admin. Every one of them is a glass card floating on the same
 * six-subject aurora as the sign-in card, so the front door reads as one place.
 */

import { useEffect, useState } from 'react';
import { Loader2, MailCheck, ShieldAlert, RefreshCw, Hourglass } from 'lucide-react';
import { cn } from '../../lib/utils';
import { LearnRoot, LearnTopBar, glassPanel, glassChip, inkMuted, useLearnTheme } from './learnShared';
import { useLearnAuth } from './LearnAuthProvider';
import { LearnSignIn, LEARN_SUBJECTS } from './LearnSignIn';
import { resendLearnerVerification } from '../../services/learnFirebase';
import { SchoolCodeForm } from './SchoolCodeForm';

const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] ' +
  'focus-visible:outline-[#5B2EE5] dark:focus-visible:outline-[#A78BFA]';

const PRIMARY = cn(
  'inline-flex h-11 items-center justify-center rounded-full bg-[#5B2EE5] px-6 text-[14px] font-semibold text-white',
  'shadow-[0_8px_20px_-8px_rgba(91,46,229,0.75)] transition-colors hover:bg-[#4D22D2]',
  FOCUS,
);

const QUIET = cn(
  'rounded-md text-[13.5px] transition-colors hover:text-[#5B2EE5] dark:hover:text-[#B49BFF]',
  FOCUS,
);

export function LearnAuthGuard({ children }: { children: React.ReactNode }) {
  const { loading, user, profile, blocked } = useLearnAuth();
  const { theme, toggle } = useLearnTheme();

  if (loading) {
    return (
      <LearnRoot theme={theme} subjects={LEARN_SUBJECTS}>
        <div className="flex h-full items-center justify-center">
          <div className={cn(glassPanel, 'grid h-16 w-16 place-items-center rounded-[22px]')}>
            <Loader2 className="h-6 w-6 animate-spin text-[#5B2EE5] dark:text-[#B49BFF]" />
          </div>
          <span className="sr-only">Loading</span>
        </div>
      </LearnRoot>
    );
  }

  if (!user) {
    return (
      <LearnRoot theme={theme} subjects={LEARN_SUBJECTS}>
        <LearnTopBar theme={theme} onToggle={toggle} />
        <LearnSignIn />
      </LearnRoot>
    );
  }

  if (blocked === 'unverified') {
    return (
      <LearnRoot theme={theme} subjects={LEARN_SUBJECTS}>
        <LearnTopBar theme={theme} onToggle={toggle} />
        <VerifyEmailScreen />
      </LearnRoot>
    );
  }

  if (blocked === 'pending') {
    return (
      <LearnRoot theme={theme} subjects={LEARN_SUBJECTS}>
        <LearnTopBar theme={theme} onToggle={toggle} />
        <PendingApprovalScreen />
      </LearnRoot>
    );
  }

  if (blocked === 'disabled') {
    return (
      <LearnRoot theme={theme} subjects={LEARN_SUBJECTS}>
        <LearnTopBar theme={theme} onToggle={toggle} />
        <Centered
          icon={<ShieldAlert className="h-7 w-7 text-[#A32B16] dark:text-[#FF9C87]" />}
          title="This account has been disabled"
          body="Your administrator can restore access. Your progress stays safe in the meantime."
        />
      </LearnRoot>
    );
  }

  // Signed in but the profile call failed for another reason (network, 500).
  // Better to say so than to render an empty catalogue that looks like data loss.
  if (!profile) {
    return (
      <LearnRoot theme={theme} subjects={LEARN_SUBJECTS}>
        <LearnTopBar theme={theme} onToggle={toggle} />
        <Centered
          icon={<RefreshCw className="h-7 w-7 text-[#5B2EE5] dark:text-[#B49BFF]" />}
          title="We could not load your account"
          body="Check your connection and reload the page."
        />
      </LearnRoot>
    );
  }

  return <>{children}</>;
}

function PendingApprovalScreen() {
  const { user, refresh, signOut } = useLearnAuth();
  const [checking, setChecking] = useState(false);

  return (
    <Centered
      icon={<Hourglass className="h-7 w-7 text-[#5B2EE5] dark:text-[#B49BFF]" />}
      title="You're on the list"
      body={`We've saved your place for ${user?.email ?? 'your account'}. If your school gave you a code, enter it to open your lessons now. Otherwise an administrator reviews new accounts.`}
    >
      <SchoolCodeForm className="mt-7" />
      <div className="mt-6 flex flex-col items-center gap-3">
        <button
          onClick={async () => {
            setChecking(true);
            await refresh();
            setChecking(false);
          }}
          className={PRIMARY}
        >
          {checking ? 'Checking…' : 'Check again'}
        </button>
        <button onClick={() => void signOut()} className={cn(QUIET, inkMuted)}>
          Use a different account
        </button>
      </div>
    </Centered>
  );
}

function VerifyEmailScreen() {
  const { user, refresh, signOut } = useLearnAuth();
  const [sent, setSent] = useState(false);
  const [checking, setChecking] = useState(false);

  // Verification happens in another tab, so poll when this one regains focus
  // rather than making the learner hunt for a "I've verified" button.
  useEffect(() => {
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  return (
    <Centered
      icon={<MailCheck className="h-7 w-7 text-[#0E6B5F] dark:text-[#6FD6C4]" />}
      title="Confirm your email"
      body={`We sent a link to ${user?.email ?? 'your inbox'}. Open it, then come back to this tab.`}
    >
      <div className="mt-7 flex flex-col items-center gap-3">
        <button
          onClick={async () => {
            setChecking(true);
            await refresh();
            setChecking(false);
          }}
          className={PRIMARY}
        >
          {checking ? 'Checking…' : "I've confirmed it"}
        </button>
        <button
          onClick={async () => {
            await resendLearnerVerification();
            setSent(true);
          }}
          className={cn(QUIET, 'font-semibold', inkMuted)}
        >
          {sent ? 'Link sent again' : 'Resend the link'}
        </button>
        <button onClick={() => void signOut()} className={cn(QUIET, inkMuted)}>
          Use a different account
        </button>
      </div>
    </Centered>
  );
}

function Centered({
  icon,
  title,
  body,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-[calc(100vh-80px)] items-center justify-center px-5 py-10 sm:py-14">
      <div className={cn(glassPanel, 'w-full max-w-[460px] px-6 py-9 text-center sm:px-9')}>
        <div className={cn(glassChip, 'mx-auto mb-6 grid h-14 w-14 place-items-center rounded-2xl bg-white/85 dark:bg-white/[0.10]')}>
          {icon}
        </div>
        <h1 className="font-serif text-[30px] leading-[1.16] tracking-[-0.015em] sm:text-[33px]">{title}</h1>
        <p className={cn('mx-auto mt-3 max-w-[40ch] text-[15px] leading-relaxed', inkMuted)}>{body}</p>
        {children}
      </div>
    </div>
  );
}
